import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Text, Title, Badge, SimpleGrid, Button } from '@repletoryreach/sdk-ui';
import { Alert, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatMinor, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { AddCreditDrawer } from './AddCreditDrawer';

/**
 * The tenant's wallet as the alert worker sees it: balance against the line,
 * who is told, auto top-up, the month's spend and the platform's margin on
 * it, and the last alerts. What an operator needs when a tenant asks "why
 * did sending stop" or "why was my card charged".
 */
export function WalletCard({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [crediting, setCrediting] = useState(false);
  const { data, isPending, error } = useQuery({ queryKey: ['admin', 'wallet-alert', tenantId], queryFn: () => admin.getWalletAlert(tenantId), retry: false });
  const w = data?.data;
  // The platform's own business (owner, 11 October 2026): marked, and still charged, so its plan is a live test.
  const house = useMutation({
    mutationFn: (on: boolean) => admin.setHouseAccount(tenantId, on),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'wallet-alert', tenantId] });
      qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      notifySuccess(res.data.house_account ? t('tenants.view.wallet.house.on') : t('tenants.view.wallet.house.off'), t('tenants.view.wallet.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('tenants.view.wallet.title'))
  });

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Title order={4}>{t('tenants.view.wallet.title')}</Title>
          <Group gap="sm">
            {w && (
              <Badge size="sm" variant="light" color={w.state === 'empty' ? 'red' : w.state === 'low' ? 'orange' : 'green'}>
                {t(`billing.walletAlerts.state.${w.state}`, w.state)}
              </Badge>
            )}
            {/* A business with no wallet yet gets one with its first credit. */}
            <Button size="xs" onClick={() => setCrediting(true)} disabled={isPending} data-testid="credit-open">{t('tenants.view.credit.open')}</Button>
          </Group>
        </Group>
        <AddCreditDrawer opened={crediting} onClose={() => setCrediting(false)} organisationId={tenantId} organisationName={tenantName}
          balanceMinor={w?.balance_minor ?? 0} currency={w?.currency ?? 'ZAR'} />
        {error && <Alert color="warning">{t('tenants.view.wallet.none')}</Alert>}
        {w && (
          <Switch
            checked={Boolean(w.house_account)}
            onChange={(on) => house.mutate(on)}
            disabled={house.isPending}
            label={t('tenants.view.wallet.house.label')}
            description={t('tenants.view.wallet.house.hint')}
          />
        )}
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
          <MetricCard label={t('tenants.view.wallet.balance')} value={w ? formatMinor(w.balance_minor, w.currency) : '–'} hint={w ? t('tenants.view.wallet.line', { amount: formatMinor(w.low_balance_minor, w.currency) }) : undefined} loading={isPending} />
          <MetricCard label={t('tenants.view.wallet.spentMtd')} value={w ? formatMinor(w.spent_month_to_date_minor, w.currency) : '–'} loading={isPending} />
          <MetricCard label={t('tenants.view.wallet.costMtd')} value={w ? formatMinor(w.cost_month_to_date_minor, w.currency) : '–'} loading={isPending} />
          <MetricCard label={t('tenants.view.wallet.marginMtd')} value={w ? formatMinor(w.margin_month_to_date_minor, w.currency) : '–'} loading={isPending} />
        </SimpleGrid>
        {w && (
          <Stack gap={4}>
            <Text role="bodySmall">
              {w.auto_topup_enabled
                ? t('tenants.view.wallet.autoOn', { amount: formatMinor(w.auto_topup_amount_minor ?? 0, w.currency), threshold: formatMinor(w.auto_topup_threshold_minor ?? w.low_balance_minor, w.currency), method: w.payment_method ?? t('common.none') })
                : t('tenants.view.wallet.autoOff', { method: w.payment_method ?? t('common.none') })}
            </Text>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('tenants.view.wallet.notify', { email: w.notify_email ?? t('billing.walletAlerts.notifyDefault'), msisdn: w.notify_msisdn ?? t('common.none') })}
              {w.daily_spend_alert_minor != null && <> · {t('tenants.view.wallet.dailyLine', { amount: formatMinor(w.daily_spend_alert_minor, w.currency) })}</>}
              {w.last_evaluated_at && <> · {t('tenants.view.wallet.evaluated', { when: formatRelative(w.last_evaluated_at) })}</>}
            </Text>
          </Stack>
        )}
        {w && w.notifications.length > 0 && (
          <Stack gap={4}>
            <Text role="bodySmall" fw={600}>{t('tenants.view.wallet.recentAlerts')}</Text>
            {w.notifications.slice(0, 8).map((n) => (
              <Group key={n.id} justify="space-between" wrap="nowrap" gap="md">
                <Text role="caption" truncate>
                  {t(`billing.walletAlerts.kind.${n.kind}`, n.kind)} · {t(`billing.walletAlerts.channel.${n.channel}`, n.channel)}{n.target ? ` · ${n.target}` : ''}
                </Text>
                <Group gap="xs" wrap="nowrap">
                  <Badge size="xs" variant="light" color={n.status === 'sent' ? 'green' : n.status === 'failed' ? 'red' : 'gray'}>{n.status}</Badge>
                  <Text role="caption" c="var(--rr-color-text-muted)" title={formatDateTime(n.created_at)}>{formatRelative(n.created_at)}</Text>
                </Group>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
